import Link from 'next/link';
import OpenModal from 'app/components/VideoModal/OpenModal';
import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';
import { getContentoVideoId, getContentUrl } from '../../utils/contentoUtils';
import { fontWeightBold } from '../design-tokens';
import { PlayBadge, PLAY_BADGE_SIZE, PLAY_BADGE_THEME } from '../PlayBadge/PlayBadge';
import { StyledAnchor } from '../PlayButton/PlayButton.styles';
import {
    StyledCampaignLink,
    StyledButtonText,
    StyledCampaignHeader,
    StyledCampaignHero,
    StyledCampaignHeroVideo,
    StyledCampaignHeroVideoContainer,
    StyledCampaingHeroInner,
    StyledSubHeader,
    EuroIcon,
    PlaySpan,
    StyledButtonTextStart,
    ButtonContainer,
    ButtonSpacer,
} from './CampaignHero.styles';
import env from 'app/env';
import { triggerPoll } from 'app/utils/hotjar';
import { useLocalStorage } from 'app/hooks/useLocalStorage';
import { BUTTON_THEME } from '../PlayButton/PlayButton';

const CampaignHero = ({ item, onAfterClick, currentAccessibilityChoiceContento, wasForced = false }) => {
    const videoRef = useRef();
    const [videoLoaded, setVideoLoaded] = useState(false);
    const [displayed, setDisplayed] = useLocalStorage('campaignSurveyEuro2020', false);

    useEffect(() => {
        const videoPlayer = videoRef.current;
        const canPlayListener = () => {
            const match = window.matchMedia('(prefers-reduced-motion: reduce)');
            if (!match.matches) {
                setVideoLoaded(true);
            }
        };

        videoPlayer?.addEventListener('canplay', canPlayListener);

        return () => {
            videoPlayer.removeEventListener('canplay', canPlayListener);
        };
    }, [videoRef, setVideoLoaded]);

    // We dont want to listen to displayed, since that would automatically close survey
    useEffect(() => {
        if (!displayed) {
            triggerPoll('trigger_campaign_survey');
            setDisplayed(true);
        }
    }, []); // eslint-disable-line

    if (!item) {
        return null;
    }

    const videoUrl = getContentUrl(item.item, currentAccessibilityChoiceContento);
    const videoId = getContentoVideoId(item.item, currentAccessibilityChoiceContento);
    const heading = item.heading;
    const subHeading = item.subHeading;
    const extraModalQuery = wasForced ? { forceCampaign: true } : null;

    return (
        <StyledCampaignHero>
            <StyledCampaingHeroInner>
                <EuroIcon />
                {item.badge && (
                    <PlayBadge
                        text={item?.badge?.text}
                        theme={item?.badge?.type}
                        accessibilityText={item?.badge?.altText}
                        size={PLAY_BADGE_SIZE.XLARGE}
                    />
                )}

                <Link href={item.item.urls.svtplay} passHref>
                    <StyledCampaignLink
                        onClick={(e) => {
                            onAfterClick(e, 1, item.item.svtId, false);
                        }}
                    >
                        <StyledCampaignHeader>{heading}</StyledCampaignHeader>
                    </StyledCampaignLink>
                </Link>
                <StyledSubHeader>{subHeading}</StyledSubHeader>

                <ButtonContainer>
                    <OpenModal videoId={videoId} videoUrl={videoUrl} extraQuery={extraModalQuery}>
                        {(onClick) => (
                            <StyledAnchor
                                theme={BUTTON_THEME.WHITE}
                                onClick={(e) => {
                                    onAfterClick(e, 1, item.item.svtId, true);
                                    onClick();
                                }}
                            >
                                <StyledButtonText weight={fontWeightBold}>Spela</StyledButtonText>
                            </StyledAnchor>
                        )}
                    </OpenModal>

                    {item?.badge?.type === PLAY_BADGE_THEME.LIVE && (
                        <>
                            <ButtonSpacer />
                            <OpenModal
                                videoId={videoId}
                                videoUrl={videoUrl}
                                position={'1'}
                                extraQuery={extraModalQuery}
                            >
                                {(onClick) => (
                                    <StyledAnchor
                                        aria-label={'Spela direktsändning från början'}
                                        onClick={(e) => {
                                            onAfterClick(e, 1, item.item.svtId, true);
                                            onClick();
                                        }}
                                    >
                                        <StyledButtonTextStart weight={fontWeightBold}>
                                            <PlaySpan
                                                dangerouslySetInnerHTML={{
                                                    __html: `Spela&nbsp;`,
                                                }}
                                            />
                                            från början
                                        </StyledButtonTextStart>
                                    </StyledAnchor>
                                )}
                            </OpenModal>
                        </>
                    )}
                </ButtonContainer>
            </StyledCampaingHeroInner>
            <StyledCampaignHeroVideoContainer>
                <StyledCampaignHeroVideo
                    id="campaign-hero-video"
                    muted
                    autoPlay
                    playsInline
                    loop
                    loaded={videoLoaded}
                    ref={videoRef}
                    data-rt="display-window-big-image"
                    aria-hidden
                >
                    <source src={env.DISPLAY_WINDOW_EURO_CLIP} type="video/mp4" />
                </StyledCampaignHeroVideo>
            </StyledCampaignHeroVideoContainer>
        </StyledCampaignHero>
    );
};

CampaignHero.propTypes = {
    item: PropTypes.object.isRequired,
    onAfterClick: PropTypes.func,
    currentAccessibilityChoiceContento: PropTypes.string,
};

export default CampaignHero;
